import { Box, Dialog, Button, styled, keyframes } from "@mui/material";
import { colors } from '../../../theme/colors';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const AssessmentPageWrapper = styled(Box)({
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#f5f6fa",
  overflow: "hidden",
});

export const AssessmentHeaderWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 20px",
  backgroundColor: "#fff",
  borderBottom: "2px solid #e8ecf1",
  height: "60px",
  flexShrink: 0,

  "& .header-left": {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  "& .header-logo": {
    height: "43px",
    width: "auto",
  },
  "& .header-title": {
    fontSize: "16px",
    fontWeight: 700,
    color: colors.primary,
    letterSpacing: "0.3px",
  },
  "& .header-center": {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  "& .webcam-preview": {
    width: "56px",
    height: "42px",
    borderRadius: "8px",
    overflow: "hidden",
    border: `2px solid ${colors.primary}`,
    "& video": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transform: "scaleX(-1)",
    },
  },
  "& .header-right": {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  "& .timer-display": {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "18px",
    fontWeight: 700,
    fontFamily: "'Courier New', monospace",
    letterSpacing: "1px",
  },
});

export const AssessmentBodyWrapper = styled(Box)({
  display: "flex",
  flex: 1,
  overflow: "hidden",
});

export const QuestionSidebarWrapper = styled(Box)({
  width: "52px",
  backgroundColor: "#fff",
  borderRight: "1px solid #e8ecf1",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  paddingTop: "12px",
  gap: "6px",
  flexShrink: 0,
});

export const QuestionNavButton = styled(Button)(({ active, saved }) => ({
  minWidth: "38px",
  width: "38px",
  height: "38px",
  borderRadius: "10px",
  fontSize: "14px",
  fontWeight: 700,
  padding: 0,
  border: active
    ? `2px solid ${colors.primary}`
    : saved
    ? "2px solid #43a047"
    : "1px solid #d0d5dd",
  backgroundColor: active ? colors.primary : saved ? "#e8f5e9" : "#fff",
  color: active ? "#fff" : saved ? "#2e7d32" : "#344054",
  "&:hover": {
    backgroundColor: active ? colors.primary : "#f0f4f8",
    border: active ? `2px solid ${colors.primary}` : `1px solid ${colors.primary}`,
  },
}));

export const QuestionPanelWrapper = styled(Box)({
  width: "42%",
  minWidth: "340px",
  maxWidth: "520px",
  backgroundColor: "#fff",
  borderRight: "1px solid #e8ecf1",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",

  "& .question-header": {
    padding: "16px 20px 12px",
    borderBottom: "1px solid #f0f2f5",
  },
  "& .question-title": {
    fontSize: "18px",
    fontWeight: 700,
    color: "#1d2939",
    marginTop: "8px",
    lineHeight: 1.4,
  },
  "& .question-body": {
    flex: 1,
    overflowY: "auto",
    padding: "16px 20px",
    fontSize: "14px",
    lineHeight: 1.7,
    color: "#344054",
    whiteSpace: "pre-wrap",

    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-thumb": {
      background: "#c4c9d4",
      borderRadius: "3px",
    },
  },
  "& .sample-section": {
    padding: "12px 16px",
    margin: "12px 0",
    backgroundColor: "#f8f9fc",
    borderRadius: "8px",
    border: "1px solid #e8ecf1",
  },
  "& .sample-label": {
    fontSize: "12px",
    fontWeight: 700,
    color: "#667085",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "4px",
  },
  "& .sample-value": {
    fontSize: "13px",
    fontFamily: "'Courier New', monospace",
    color: "#1d2939",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
});

export const CodeEditorWrapper = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  backgroundColor: "#1e1e1e",

  "& .editor-toolbar": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 12px",
    backgroundColor: "#2d2d2d",
    borderBottom: "1px solid #3e3e3e",
  },
  "& .editor-lang-badge": {
    fontSize: "12px",
    fontWeight: 600,
    color: "#ccc",
    textTransform: "capitalize",
  },
  "& .editor-actions": {
    display: "flex",
    gap: "8px",
  },
  "& .editor-container": {
    flex: 1,
    overflow: "hidden",
  },
});

export const BottomBarWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "12px",
  padding: "10px 20px",
  backgroundColor: "#fff",
  borderTop: "2px solid #e8ecf1",
  flexShrink: 0,
});

export const StartPageWrapper = styled(Box)({
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  background: `linear-gradient(to bottom, ${colors.primaryLight}, ${colors.white})`,
  overflowY: "auto",

  "& .start-header": {
    display: "flex",
    alignItems: "center",
    padding: "16px 32px",
    borderBottom: "1px solid #e8ecf1",
    backgroundColor: "#fff",
    "& img": { height: "43px", width: "auto" },
    "& .header-text": {
      marginLeft: "16px",
      fontSize: "18px",
      fontWeight: 700,
      color: colors.primary,
    },
  },
  "& .start-body": {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
  },
  "& .start-card": {
    maxWidth: "640px",
    width: "100%",
    padding: "40px",
    borderRadius: "20px",
    backgroundColor: "#fff",
    boxShadow: "0 8px 40px rgba(127, 29, 29, 0.08)",
    animation: `${fadeUp} 0.5s ease-out`,
  },
  "& .start-title": {
    fontSize: "26px",
    fontWeight: 800,
    color: colors.primary,
    marginBottom: "8px",
  },
  "& .start-subtitle": {
    fontSize: "15px",
    color: "#667085",
    marginBottom: "24px",
  },
  "& .guidelines-list": {
    paddingLeft: "20px",
    marginBottom: "24px",
    "& li": {
      fontSize: "14px",
      color: "#344054",
      marginBottom: "10px",
      lineHeight: 1.6,
    },
  },
  "& .countdown-box": {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 20px",
    borderRadius: "12px",
    backgroundColor: colors.primaryLight,
    border: "1px solid #fecaca",
    marginBottom: "24px",
  },
  "& .agree-row": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "24px",
  },
});

export const GuidelinesDialog = styled(Dialog)({
  "& .MuiPaper-root": {
    borderRadius: "16px",
    maxWidth: "560px",
    width: "100%",
  },
  "& .guidelines-header": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    backgroundColor: colors.primary,
    color: "#fff",
  },
  "& .guidelines-body": {
    padding: "24px",
    "& ol": {
      paddingLeft: "20px",
      "& li": {
        fontSize: "14px",
        color: "#344054",
        marginBottom: "12px",
        lineHeight: 1.6,
      },
    },
  },
  "& .guidelines-footer": {
    padding: "12px 24px 20px",
    display: "flex",
    justifyContent: "center",
  },
});

export const ConfirmSubmitDialog = styled(Dialog)({
  "& .MuiPaper-root": {
    borderRadius: "16px",
    maxWidth: "440px",
    width: "100%",
    padding: "32px",
    textAlign: "center",
  },
  "& .confirm-icon": {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    backgroundColor: "#fff3e0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  },
  "& .confirm-title": {
    fontSize: "20px",
    fontWeight: 700,
    color: "#1d2939",
    marginBottom: "8px",
  },
  "& .confirm-message": {
    fontSize: "14px",
    color: "#667085",
    marginBottom: "24px",
    lineHeight: 1.6,
  },
  "& .confirm-actions": {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
  },
});

export const CameraMessageDialog = styled(Dialog)({
  "& .MuiPaper-root": {
    borderRadius: "12px",
    maxWidth: "480px",
    width: "100%",
    overflow: "hidden",
  },
  "& .camera-header": {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "14px 20px",
    backgroundColor: colors.secondary,
    color: "#fff",
  },
  "& .camera-body": {
    padding: "20px 24px",
    fontSize: "15px",
    color: "#344054",
    lineHeight: 1.6,
  },
});
